import { useEffect, useState } from "react";
import api from "@/lib/api";

const POLL_MS = 30000;
const STALE_MS = 10000;

// One shared presence store for every page, so all green/yellow dots always agree.
let state = { people: [], online: new Map(), ready: false };
let loadedAt = 0;
let timer = null;
let inflight = null;
const subs = new Set();

const load = () => {
  if (inflight) return inflight;
  inflight = api.get("/users/online").then((r) => {
    state = { people: r.data, online: new Map(r.data.map((u) => [u.id, u.presence])), ready: true };
    loadedAt = Date.now();
    subs.forEach((fn) => fn(state));
  }).catch(() => {}).finally(() => { inflight = null; });
  return inflight;
};

const onVisible = () => {
  if (document.visibilityState === "visible") load();
};

const start = () => {
  timer = setInterval(() => document.visibilityState === "visible" && load(), POLL_MS);
  document.addEventListener("visibilitychange", onVisible);
};

const stop = () => {
  clearInterval(timer);
  document.removeEventListener("visibilitychange", onVisible);
};

// { people: [{id, name, role, presence}], online: Map(id -> "online" | "away") }
export function useOnlineState() {
  const [s, setS] = useState(state);
  useEffect(() => {
    subs.add(setS);
    if (subs.size === 1) start();
    // Opening a page refreshes presence if it's more than a few seconds old.
    if (Date.now() - loadedAt > STALE_MS) load();
    else setS(state);
    return () => {
      subs.delete(setS);
      if (!subs.size) stop();
    };
  }, []);
  return s;
}

export function useOnline() {
  return useOnlineState().online;
}

// Presence for any user id; you are always online to yourself.
export function presenceFor(online, id, meId) {
  if (!id) return undefined;
  return id === meId ? "online" : online.get(id) || "offline";
}
